import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';
import { Bell, Lock, Smartphone, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Phase 7-a UX (Feb 2026): server-side notification preferences.
//
//   • Backend is source of truth: PATCH /users/me/preferences.
//   • Auth response returns a resolved `preferences.notifications` object so
//     the client always has every key, even for legacy users.
//   • Any legacy per-device value stored under `sc365_notif_prefs_<userId>`
//     is migrated up to the server on first load, then the localStorage
//     entry is deleted so the client stays in sync across devices.
//   • Operational alerts + security are hard-locked to "on". The backend
//     enforces this as well.

const LEGACY_LS_KEY = (uid) => `sc365_notif_prefs_${uid}`;

// Category definitions — schema keys mirror the backend `preferences.notifications` shape.
const WORKER_CATEGORIES = [
  { key: 'shiftOpportunities', label: 'Shift opportunities', description: 'New shifts posted near you.', optional: true },
  { key: 'applications', label: 'Applications and confirmations', description: 'When a manager reviews or picks you for a shift.', optional: true },
  { key: 'shiftReminders', label: 'Shift reminders', description: 'Heads-up before your shift starts.', optional: true },
  { key: 'messages', label: 'Messages', description: 'Direct messages from managers about your shifts.', optional: true },
  { key: 'operationalAlerts', label: 'Operational alerts', description: 'Shift cancellations, changes, till-check issues.', optional: false },
  { key: 'productUpdates', label: 'Product updates', description: 'New features and improvements — never more than monthly.', optional: true },
  { key: 'marketing', label: 'Marketing communications', description: 'Tips, offers and community news. Off by default.', optional: true },
];

const MANAGER_CATEGORIES = [
  { key: 'shiftOpportunities', label: 'Worker availability', description: 'When workers apply to your shifts.', optional: true },
  { key: 'applications', label: 'Applications and confirmations', description: 'Workers accepting or declining chosen shifts.', optional: true },
  { key: 'shiftReminders', label: 'Shift reminders', description: 'Heads-up before your team\'s shifts start.', optional: true },
  { key: 'messages', label: 'Messages', description: 'Direct messages from your workers.', optional: true },
  { key: 'operationalAlerts', label: 'Operational alerts', description: 'Till discrepancies, no-shows, payment issues.', optional: false },
  { key: 'productUpdates', label: 'Product updates', description: 'New features and improvements — never more than monthly.', optional: true },
  { key: 'marketing', label: 'Marketing communications', description: 'Tips, offers and community news. Off by default.', optional: true },
];

const MANDATORY_ROWS = [
  { key: 'security', label: 'Security and verification', description: 'Sign-in alerts, password changes, verification updates.' },
  { key: 'essential', label: 'Essential service notices', description: 'Terms updates, downtime warnings, legally required notices.' },
];

// Legacy → new schema key rename map so users don't lose their choice on
// the migration. Old localStorage keys (opportunities/applications/etc.)
// map onto the new camelCase server keys 1:1 where the semantics match.
const LEGACY_KEY_MAP = {
  opportunities: 'shiftOpportunities',
  applications: 'applications',
  reminders: 'shiftReminders',
  messages: 'messages',
  operational: 'operationalAlerts',
  product: 'productUpdates',
  marketing: 'marketing',
};

export default function NotificationSettings() {
  const { api, user, refreshUser } = useAuth();
  const categories = useMemo(
    () => (user?.role === 'manager' ? MANAGER_CATEGORIES : WORKER_CATEGORIES),
    [user?.role]
  );

  const serverPrefs = user?.preferences?.notifications;
  const [prefs, setPrefs] = useState(() => ({ ...(serverPrefs || {}) }));
  const [pendingKey, setPendingKey] = useState(null);
  const migratedRef = useRef(false);

  // Keep local mirror in sync with the auth-context user object.
  useEffect(() => {
    if (serverPrefs) setPrefs({ ...serverPrefs });
  }, [serverPrefs]);

  // One-off migration: push any legacy localStorage prefs up to the server,
  // then remove the local copy so we don't drift across devices.
  useEffect(() => {
    if (!user?.id || migratedRef.current) return;
    migratedRef.current = true;
    let legacyRaw;
    try { legacyRaw = localStorage.getItem(LEGACY_LS_KEY(user.id)); } catch { return; }
    if (!legacyRaw) return;
    let legacy;
    try { legacy = JSON.parse(legacyRaw); } catch { return; }
    // Translate legacy keys to the new schema; skip operational (locked on).
    const payload = {};
    Object.entries(legacy || {}).forEach(([oldKey, val]) => {
      const newKey = LEGACY_KEY_MAP[oldKey];
      if (!newKey || newKey === 'operationalAlerts') return;
      payload[newKey] = !!val;
    });
    if (Object.keys(payload).length === 0) {
      try { localStorage.removeItem(LEGACY_LS_KEY(user.id)); } catch { /* ignore */ }
      return;
    }
    (async () => {
      try {
        await api.patch('/users/me/preferences', { notifications: payload });
        if (typeof refreshUser === 'function') await refreshUser();
        try { localStorage.removeItem(LEGACY_LS_KEY(user.id)); } catch { /* ignore */ }
      } catch { /* leave the local copy so we can retry next visit */ }
    })();
  }, [user?.id, api, refreshUser]);

  const toggle = async (key, currentlyOn) => {
    // Optimistic UI — flip immediately, revert on failure.
    setPendingKey(key);
    const next = { ...prefs, [key]: !currentlyOn };
    setPrefs(next);
    try {
      await api.patch('/users/me/preferences', { notifications: { [key]: !currentlyOn } });
      if (typeof refreshUser === 'function') await refreshUser();
    } catch {
      setPrefs(prefs); // revert
      toast.error('Could not save that change. Please try again.');
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="notification-settings">
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-[#0f1b3d]/10 flex items-center justify-center">
              <Bell className="h-5 w-5 text-[#0f1b3d]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Notification categories</h2>
              <p className="text-sm text-slate-500">Choose what we send. Preferences sync across your devices.</p>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {categories.map((c) => {
              const on = c.optional ? !!prefs[c.key] : true;
              const busy = pendingKey === c.key;
              return (
                <div key={c.key} className="py-3.5 flex items-start justify-between gap-3" data-testid={`notif-row-${c.key}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-medium text-slate-900">{c.label}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{c.description}</p>
                    {!c.optional && (
                      <p className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1" data-testid={`notif-mandatory-${c.key}`}>
                        <Lock className="h-3 w-3" /> Always on — critical for your account
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                    <Switch
                      checked={on}
                      onCheckedChange={() => c.optional && toggle(c.key, on)}
                      disabled={!c.optional || busy}
                      aria-label={`Toggle ${c.label}`}
                      data-testid={`notif-toggle-${c.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2.5">Always on</p>
            <div className="space-y-2.5">
              {MANDATORY_ROWS.map((r) => (
                <div key={r.key} className="flex items-start gap-2.5" data-testid={`notif-always-${r.key}`}>
                  <Lock className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{r.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Device-side instructions (unchanged from Phase 6) */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-xl bg-[#4338CA]/10 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-[#4338CA]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Enable on your device</h2>
              <p className="text-sm text-slate-500">If you don&apos;t see notifications, check your phone settings.</p>
            </div>
          </div>
          <Accordion type="single" collapsible className="mt-3" data-testid="device-instructions">
            <AccordionItem value="ios" className="border-slate-200">
              <AccordionTrigger className="text-base font-medium hover:no-underline min-h-[48px] py-3">
                <div className="flex items-center gap-2"><ChevronRight className="h-4 w-4" /> iPhone (iOS)</div>
              </AccordionTrigger>
              <AccordionContent className="pl-6 text-sm text-slate-600 space-y-1">
                <p>1. Open <strong>Settings</strong> on your iPhone.</p>
                <p>2. Tap <strong>Notifications</strong>.</p>
                <p>3. Scroll to <strong>ShiftCover365</strong> and tap it.</p>
                <p>4. Turn <strong>Allow Notifications</strong> on.</p>
                <p>5. Choose Banners, Sounds and Badges as you prefer.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="android" className="border-slate-200">
              <AccordionTrigger className="text-base font-medium hover:no-underline min-h-[48px] py-3">
                <div className="flex items-center gap-2"><ChevronRight className="h-4 w-4" /> Android</div>
              </AccordionTrigger>
              <AccordionContent className="pl-6 text-sm text-slate-600 space-y-1">
                <p>1. Open <strong>Settings</strong> on your phone.</p>
                <p>2. Tap <strong>Apps</strong> (or <strong>Notifications</strong>).</p>
                <p>3. Find <strong>ShiftCover365</strong> in the list.</p>
                <p>4. Tap <strong>Notifications</strong> and switch them on.</p>
                <p>5. Fine-tune categories if your phone supports them.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
